import type { ReactElement } from "react";
import classNames from "classnames";
import type { IconType } from "@circle-react-shared/Icon";
import { Icon } from "@circle-react-shared/Icon";
import { Table } from "@circle-react-uikit/TableV2";
import { useDataTableDataProvider } from "../Provider/DataTableDataProvider";
import { HeaderText } from "./HeaderText";

export interface SortableTableHeadProps {
  ascendingIcon?: IconType;
  descendingIcon?: IconType;
  header: any;
}

export const SortableTableHead = ({
  header,
  ascendingIcon = "12-arrow-up",
  descendingIcon = "12-arrow-down",
}: SortableTableHeadProps) => {
  const { isLoading } = useDataTableDataProvider();

  if (header.isPlaceholder) return null;

  const sortIcons = {
    asc: <Icon type={ascendingIcon} size={12} className="text-dark" />,
    desc: <Icon type={descendingIcon} size={12} className="text-dark" />,
  };

  const sortKey = header.column.getIsSorted() as keyof typeof sortIcons;
  const sortIcon: ReactElement | null = sortIcons[sortKey] || null;

  // eslint-disable-next-line sonarjs/no-inverted-boolean-check -- Disabled for plugin version update. Fix when possible
  const isColumnSortable = !(header.column.columnDef.canSort === false);

  return (
    <Table.Th
      className={classNames(
        {
          "hover:bg-secondary cursor-pointer select-none transition-colors":
            isColumnSortable && !isLoading,
          "opacity-30": isColumnSortable && isLoading,
        },
        header.column.columnDef.thClassName,
      )}
      {...(isColumnSortable &&
        !isLoading && {
          onClick: header.column.getToggleSortingHandler(),
        })}
    >
      {isColumnSortable ? (
        <span className="flex items-center">
          <HeaderText header={header} />
          <span
            className={classNames("flex size-4 items-center", {
              invisible: Boolean(!sortIcon),
            })}
          >
            {sortIcon}
          </span>
        </span>
      ) : (
        <HeaderText header={header} />
      )}
    </Table.Th>
  );
};
